import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as PR from "../../../../prime-react/index";
import { bellRinging } from "../../../../assets/images/index";
import "./BetDetails.scss";
import Layout from "../../../../layouts/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getBetDetailsAPI } from "../../../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../../../context/ToastContext";
import { capitalizeFirstLetterOfEachSentence, currencyFormat, DateFormat, formatTime, numberFormat } from "../../../../utils/reuse";

const BetDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const params = useParams();
  const sessionData = useSelector((state) => state.auth.sessionData);
  const headers = useMemo(() => {
    return { sessionid: sessionData.sessionId };
  }, [sessionData.sessionId]);
  const [singleBetDetails, setSingleBetDetails] = useState({});
  const { state } = useLocation();
  const [isChecked, setIsChecked] = useState(false);
  const [isVoucherBet, setVoucherBet] = useState(!!state?.isVoucherBet);
  const [backNavigation, setBackNavigation]= useState({url: 'betting-data', name : 'Betting Data'});
  useEffect(() => {
     if(state?.type === 'anomalies'){
        setBackNavigation({ url: `anomalies`, name: 'Anomalies'});
     } else if(state?.type && state?.playerId) {
        setBackNavigation({ url: `player-analytics/${state.playerId}`, name: 'Player Analytics'});
     }
  }, [state]);
  
  const getBetDetailsHandler = useCallback(() => {
    const id = params?.betId;
    const getResponse = (response) => {
      if (response.result === "SUCCESS") {
        const data = response.data ?? {};
        const isVoucherBet = !data.betID;
        setVoucherBet(isVoucherBet);
        data.stakeFormat = currencyFormat(data.ticketSold, state?.groupId);
        data.netGGRFormat = currencyFormat(data.netGGR, state?.groupId);
        data.ticketPaidFormat = currencyFormat(data.ticketPaid, state?.groupId);
        data.settlementAmountFormat = currencyFormat(data.settlementAmount, state?.groupId);
        if (isVoucherBet) {
          data.voucherPaidAmountFormat = currencyFormat(data.voucherPaidAmount, state?.groupId);
          data.voucherSoldFormat = currencyFormat(data.voucherSold, state?.groupId);
        } else {
          data.freebetsSoldFormat = currencyFormat(data.freebetsSold, state?.groupId);
          data.resettledFormat = currencyFormat(data.resettled, state?.groupId);
          data.voidedFormat = currencyFormat(data.voided, state?.groupId);
          data.cancelledFormat = currencyFormat(data.cancelled, state?.groupId);
          data.maxBetPercentage = data.maxBetPercentage ? data.maxBetPercentage + '%' : '-';
          data.totalOpenBetsSoldFormat = currencyFormat(data.totalOpenBetsSold, state?.groupId);
        }
        setSingleBetDetails(data);
      } else {
        setSingleBetDetails({})
        showToast('warn', 'No record found');
      }
    };
    getBetDetailsAPI(id, headers, dispatch, getResponse);
  }, [dispatch, headers, params?.betId, showToast, state?.groupId]);
  useEffect(() => {
    getBetDetailsHandler();
  }, [getBetDetailsHandler]);
  const dateDisplay = (rowData, paramName) => {
    let rawDate = rowData[paramName];
    const isValidDate = rawDate && rawDate !== '0001-01-01T00:00:00Z';
    return (
      <div className="flex align-items-center gap-3">
        <div className="event-column">
          {isValidDate ? (
            <>
              <p>{DateFormat(rawDate)}</p>
              {paramName !== 'reportDate' && <span>{formatTime(rawDate)}</span>}
            </>
          ) : (
            <p>-</p>
          )}
        </div>
      </div>
    );
  };
  const renderValueOrDash = (value) => {
    return value ? value : '-';
  };

  const displayMarketType = (rowData) => {
    const marketType = rowData?.marketType ? rowData.marketType : '-';
    return `${marketType}`;
  }

 const displayOdds = (rowData) => {
  const americanOdds = Number(rowData?.odds);

  if (Number.isNaN(americanOdds) || americanOdds === 0) {
    return numberFormat(0);
  }

  const decimalOdds =
    americanOdds > 0
      ? (americanOdds / 100) + 1
      : (-100 / americanOdds) + 1;

  return numberFormat(decimalOdds);
};

  return (
    <>
      <Layout backBtn={backNavigation.url} backBtnLabel={backNavigation.name}>
        <section className="alert-details-section platform-bet-details">
          <div className="m-0">
            {isVoucherBet ? <h1>Voucher Details</h1> : <h1>Bet ID - {singleBetDetails?.betID}</h1>}
          </div>
          <div className="alert-notification-section" style={{ display: 'none' }}>
            <div className="flex align-items-center justify-content-between gap-3">
              <div className="flex align-items-center gap-3">
                <PR.Image
                  src={bellRinging}
                  alt="Alert Notifications"
                  className="notifications-icon"
                />
                <h2>Stake Exceeded Threshold </h2>
              </div>
              <div className="data-p">
                <strong>Event:</strong>
                <span>Los Angeles Lakers vs. Boston Celtics</span>
              </div>
              <div className="data-p">
                <strong>Location:</strong>
                <span>Tulalip Sportsbook</span>
              </div>
              <div className="data-p">
                <strong>Threshold Breach:</strong>
                <span>$50,000 (Normal Avg: $10,000)</span>
              </div>
              <PR.Button
                label={isChecked ? "Checked" : "Mark as Checked"}
                icon={isChecked ? "pi pi-check" : null}
                className={`view-button ${isChecked ? "checked" : ""}`}
                onClick={() => setIsChecked(prev => !prev)}
              />
            </div>
          </div>
          <div className="details-wrapper">
            <div className="details-section bet-details">
              <h3>{isVoucherBet ? "Voucher Details" : "Bet Details"}</h3>
              <div className="hr-line"></div>

              {/* Details List */}
              <div className="col-12 grid gap-6 mt-3">
                <div className="col-5 border-right">
                  <ul className="details-ul">
                    <li>Player ID</li>
                    {singleBetDetails?.playerID ? <li className="underline cursorPointer" onClick={() => navigate(`/player-analytics/${singleBetDetails.playerID}`, {state:{ betId: params?.betId}})}>{singleBetDetails?.playerID}</li> : <li>-</li>}
                  </ul>
                  <ul className="details-ul">
                    <li>Operator</li>
                    {singleBetDetails?.operator ? <li>{singleBetDetails?.cashierOperator}</li> : <li>-</li>}
                  </ul>
                  <ul className="details-ul">
                    <li>Issued Location</li>
                    {singleBetDetails?.property ? <li>{capitalizeFirstLetterOfEachSentence(singleBetDetails?.property)}</li> : <li>-</li>}
                  </ul>
                  <ul className="details-ul">
                    <li>Status</li>
                    {singleBetDetails?.status ? <li>{singleBetDetails?.status}</li> : <li>-</li>}
                  </ul>
                </div>
                <div className="col-6">
                  <ul className="details-ul">
                    <li>{isVoucherBet ? "Voucher ID" : "Ticket ID"}</li>
                    <li>{isVoucherBet ? renderValueOrDash(singleBetDetails?.voucherID) : renderValueOrDash(singleBetDetails?.ticketID)}</li>
                  </ul>
                  <ul className="details-ul">
                    <li>Channel</li>
                    <li>{renderValueOrDash(singleBetDetails?.channel)}</li>
                  </ul>
                  <ul className="details-ul">
                    <li>Bet Type</li>
                    <li>{renderValueOrDash(singleBetDetails?.betName)}</li>
                  </ul>
                  <ul className="details-ul">
                    <li>Selection</li>
                    <li>{renderValueOrDash(singleBetDetails?.selection)}</li>
                  </ul>
                </div>
              </div>

              {/* Data Table */}

              {/* Grid 1 */}
              {!isVoucherBet && <div className="col-12">
                <PR.DataTable
                  value={singleBetDetails.events}
                  stripedRows
                  className="data-table values-table"
                >
                  <PR.Column field="event" header="Event" style={{width:"30%", wordBreak:"break-word"}} />
                  <PR.Column body={(data) => dateDisplay(data, 'eventDate')} field="eventDate" header="Event Date" />
                  <PR.Column field="sport" header="Sport" />
                  <PR.Column field="tournament" header="League" />
                  <PR.Column field="market" header="Market" />
                  <PR.Column field="marketType" header="Market Type" body={displayMarketType} className="marketType" />
                  <PR.Column field="wagerDescription" header="Wager Description" />
                  <PR.Column field="odds" header="Odds" body={displayOdds} />
                </PR.DataTable>
              </div>}

              {/* Grid 2 */}
              {(singleBetDetails?.channel === 'Online') ?
                (<div className="col-12">
                  <PR.DataTable
                    value={[singleBetDetails]}
                    stripedRows
                    className="data-table date-table table-layout"
                  >
                    <PR.Column field="stakeFormat" header="Stake" />
                    <PR.Column field="netGGRFormat" header="Net GGR" />
                    <PR.Column field="ticketPaidFormat" header="Paid" />
                    <PR.Column field="freebetsSoldFormat" header="Free Bets Sold" />
                    <PR.Column field="totalOpenBetsSoldFormat" header="Total Open Bets Sold" />
                    <PR.Column field="resettledFormat" header="Resettled" />
                    <PR.Column field="voidedFormat" header="Voided" />
                    <PR.Column field="cancelledFormat" header="Cancelled" />
                    <PR.Column field="maxBetPercentage" header="Max Bet Percentage" />
                  </PR.DataTable>
                </div>)
                :
                (<div className="col-12">
                  <PR.DataTable
                    value={[singleBetDetails]}
                    stripedRows
                    className="data-table date-table table-layout"
                  >
                    <PR.Column field="stakeFormat" header="Stake" />
                    <PR.Column field="netGGRFormat" header="Net GGR" />
                    <PR.Column field="ticketPaidFormat" header="Paid" />
                    <PR.Column field="voucherSoldFormat" header="Voucher Sold" />
                    <PR.Column field="voucherPaidAmountFormat" header="Voucher Paid" />
                    <PR.Column field="settlementAmountFormat" header="Settled" />
                    <PR.Column field="maxBetPercentage" header="Max Bet Percentage" />
                  </PR.DataTable>
                </div>)
              }

              {/* Grid 3 */}
              <div className="col-12">
                <PR.DataTable
                  value={[singleBetDetails]}
                  stripedRows
                  className="data-table date-table table-layout"
                >
                  <PR.Column body={(data) => dateDisplay(data, 'reportDate')} field="reportDate" header="Report Date" />
                  <PR.Column body={(data) => dateDisplay(data, 'soldDate')} field="soldDate" header="Sold Date" />
                  <PR.Column body={(data) => dateDisplay(data, 'paidDate')} field="paidDate" header="Paid Date" />
                  {(singleBetDetails?.channel === 'Retail') && <PR.Column body={(data) => dateDisplay(data, 'voucherExpiration')} field="voucherExpiration" header="Voucher Exp. Date" />}
                  {(singleBetDetails?.channel === 'Retail') ?
                    <PR.Column body={(data) => dateDisplay(data, 'settledDate')} field="settledDate" header="Settled Date" />
                    : <PR.Column body={(data) => dateDisplay(data, 'resettledDate')} field="resettledDate" header="Resettled Date" />
                  }
                  {(singleBetDetails?.channel === 'Online') && <PR.Column body={(data) => dateDisplay(data, 'voidedCancelledDate')} field="voidedCancelledDate" header="Voided Cancelled Date" />}
                </PR.DataTable>
              </div>
              
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default BetDetails;
